import type { SOURCE, STATUS, STATUS_ID } from "@/stores/constants";
import type { LocationApiResult } from "../locations/types";
import type { VisitorFieldApiResponse, VisitorField } from "../visitorFields";
import type { QueryKey } from "@tanstack/vue-query";
import type { DeepMaybeRef } from "../common/types";
import type {
  ApiClient,
  CountVisitResponseApiResponse,
  GetVisitResponseApiResponse,
  ListVisitFieldsItem,
  ListVisitItem,
  ListVisitItemPagedApiResponse,
} from "../zoolander";

export type ListVisitsRequest = Parameters<ApiClient["listVisits"]>[0];

export type ListVisitsQuery = Omit<
  ListVisitsRequest,
  "startedAt" | "finishedAt"
> & {
  startedAt: Date;
  finishedAt: Date;
};

export type VisitWithFields = ListVisitItem & {
  fields: ListVisitFieldsItem[];
};

export type Visit = {
  id: string;
  pk: string;
  numericId: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  visitor_id: string | null;
  visit_at: string;
  visit_till: string;
  visit_fact_at: string;
  visit_fact_to: string;
  photoshot_key: string | null;
  category_name: string | null;
  location_id: string;
  preregistration_id: string | null;
  user_id: string;
  agreement_id: string | null;
  agreement_key: string | null;
  print_on_gadget_id: string | null;
  signature_key: string | null;
  is_last: boolean;
  source_in: SOURCE.CONTACTLESS;
  source_out: SOURCE;
  review_status: STATUS;
  reviewed_by: string | null;
  reviewed_at: string | null;
  anonymized_at: string | null;
  metadata: unknown;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  duration: number;
  location?: LocationApiResult;
  visit_at_unix: number | null;
  visit_till_unix: number | null;
  visit_fact_at_unix: number | null;
  visit_fact_to_unix: number | null;
  visit_fact_at_tz: string;
  visit_fact_to_tz: string | null;
  reviewer: {
    first_name: string;
    last_name: string;
  };
  reviewed_at_tz: string | null;
  location_name: string;
  time_zone: string;
  user: {
    first_name?: string;
    last_name?: string;
    anonymized_at: string | null;
    deleted_at: string | null;
  };
  customFields: VisitorField[];
  fields?: VisitorField[];
  loading: boolean;
};

export type VisitApiResponse = Omit<Visit, "customFields"> & {
  customFields: VisitorFieldApiResponse[];
};

export type VisitTrendsParams = {
  locationPk?: string;
  locationGroupPk?: string;
};

export type VisitTrends = {
  date: string;
  count: string;
};

export type VisitResponseTrends = {
  visits: VisitTrends[];
};

export type VisitTrendsResponse = {
  httpCode: number;
  data: VisitResponseTrends;
};

export type VisitResponse = {
  httpCode: number;
  visit: VisitApiResponse;
};

export type VisitorAgreementInput = {
  visitPk: string;
  name: string;
};

export type VisitorContactTraceInput = {
  visitId: string;
  name: string;
};

export type VisitorFieldValues = Record<string, string | string[]>;

export type VisitsRequest = {
  full_name: string;
  location_id: string;
  category_name: string;
  company_name?: string;
  visit_fact_at: string;
  user_id: string | number;
  [key: string]: string | number | undefined;
};

export type UpdateVisitorPayload = Pick<
  Visit,
  "full_name" | "user_id" | "company_name" | "metadata"
>;

export type ChangeVisitorStatusPayload = {
  currentStatus: STATUS_ID;
};

export type UpdateVisitorParams = {
  pk: string;
  payload: Partial<UpdateVisitorPayload & ChangeVisitorStatusPayload>;
  filters?: DeepMaybeRef<ListVisitsRequest>;
};

export type UpdateVisitorContext = {
  listQueryKey: QueryKey;
  previousListVisit?: ListVisitItemPagedApiResponse;
  byIdQueryKey: QueryKey;
  previousByIdVisit?: GetVisitResponseApiResponse;
  countQueryKey: QueryKey;
  previousCounts?: CountVisitResponseApiResponse;
};

export type UpdateVisitParams = {
  pk: string;
  payload: { review_status: STATUS.ALLOW | STATUS.DENY };
  filters: DeepMaybeRef<ListVisitsRequest>;
};

export type UpdateVisitContext = {
  listQueryKey: QueryKey;
  previousListVisit?: ListVisitItemPagedApiResponse;
  byIdQueryKey: QueryKey;
  previousByIdVisit?: GetVisitResponseApiResponse;
};

export enum TimelineActionType {
  EXPORT = "export",
  SIGN_OUT = "signOut",
  ANONYMIZE = "anonymize",
}

export type ExportSelectedVisitsPayload = { ids: string };

export type ExportFilterPayload = {
  search?: string;
  status?: string;
  category_name?: string;
  date?: string;
  review_status?: string;
  visit_movement?: string;
  startDate?: string;
  endDate?: string;
};

export type ExportPayload = ExportSelectedVisitsPayload | ExportFilterPayload;

export type FilteredSignOutAllRequestPayload = {
  filter: {
    company_name?: string;
    category_name?: string;
    full_name?: string;
    visit_fact_to?: string;
  };
};

export type UnFilteredSignOutAllRequestPayload = { all: boolean };

export type SignOutAllRequestPayload =
  | UnFilteredSignOutAllRequestPayload
  | FilteredSignOutAllRequestPayload;

export type SignOutAllVisitsParams = {
  payload: SignOutAllRequestPayload;
  queryKey: QueryKey;
  countQueryKey: QueryKey;
};

export type SignOutSelectedVisitsParams = {
  payload: string[];
  queryKey: QueryKey;
  countQueryKey: QueryKey;
};
